<template>
  <add-image v-if="imageForActions === '' && imageResult === ''"
    is-remove-bg
    is-finetune-bg
    @update:finetuneBg="fintuneBgHandler"
  >
    <div style="text-align: center; margin-bottom: 12px; color: var(--secondary-100);">{{ $t('menu.generation') }}</div>
    <img :src="`${baseUrl}/images/multi-menu/generation.png`" alt="">
  </add-image>
  <div
    v-else-if="imageForActions !== '' && step === 1"
    class="editor-canvas"
    :class="{ 'editor-canvas--mobile': isMobile }"
  >
    <div class="editor-canvas__inner">
      <ai-card :radius="16" class="editor-canvas__tools__panel">
        <div class="editor-canvas__tools">
          <div class="editor-canvas__tools__item">
            <span>{{ $t('common.brush-type') }}</span>
            <div v-if="!isMobile" class="editor-canvas__controllers">
              <ai-tooltip content-class="editor-canvas__tooltip" top location="top">
                <template #top>
                  <ai-clear-button
                    :class="{ 'editor-canvas__controller--active': mode === 'erase' }"
                    class="editor-canvas__controller"
                    @click="setMode('erase')"
                  >
                    <img :src="require('@/assets/icons/editor/removeBg.svg')" alt="#" />
                  </ai-clear-button>
                </template>
                <template #content>
                  <span>{{ $t('image-editor.erase') }}</span>
                  {{ $t('image-editor.erase-tooltip-text') }}
                </template>
              </ai-tooltip>
              <ai-tooltip content-class="editor-canvas__tooltip" top location="top">
                <template #top>
                  <ai-clear-button
                    :class="{ 'editor-canvas__controller--active': mode === 'restore' }"
                    class="editor-canvas__controller"
                    @click="setMode('restore')"
                  />
                </template>
                <template #content>
                  <span>{{ $t('image-editor.restore-brush') }}</span>
                  {{ $t('image-editor.restore-tooltip-text') }}
                </template>
              </ai-tooltip>
            </div>
            <div v-if="isMobile" class="editor-canvas__controllers">
              <ai-clear-button
                :class="{ 'editor-canvas__controller--active': mode === 'erase' }"
                class="editor-canvas__controller"
                @click="setMode('erase')"
              >
                <img :src="require('@/assets/icons/editor/removeBg.svg')" alt="#" />
              </ai-clear-button>
              <ai-clear-button
                :class="{ 'editor-canvas__controller--active': mode === 'restore' }"
                class="editor-canvas__controller"
                @click="setMode('restore')"
              />
            </div>
          </div>
          <v-spacer />
          <div class="editor-canvas__tools__item">
            <span>{{ $t('common.size') }}</span>
            <v-slider :min="0" :max="220" v-model.number="brushSize" style="width: 100px" />
          </div>
        </div>
      </ai-card>
      <ai-card :radius="16" class="editor-canvas__image">
        <div class="editor-canvas__zoom">
          <ai-clear-button class="editor-canvas__zoom__button" :active-zoom="activeZoom" @click="zoomOutHandler">
            <inline-svg :src="require('@/assets/icons/IconDecrease.svg')" />
          </ai-clear-button>
          <ai-clear-button class="editor-canvas__zoom__button" @click="zoomInHandler">
            <inline-svg :src="require('@/assets/icons/IconIncrease.svg')" />
          </ai-clear-button>
        </div>
        <div class="editor-canvas__image__container">
          <ai-canvas
            :images="[]"
            :width="imageWidth"
            :height="imageHeight"
            :background="imageForActions"
            :restore-background="sourceBgImage"
            :mode="mode"
            :key="key"
            ref="aiCanvasRef"
            :brush-size="brushSize"
            :active-zoom="activeZoom"
            @active-zoom-set="activeZoomSetHandler"
          />
        </div>
      </ai-card>
    </div>
    <div class="editor-canvas__bottom">
      <v-spacer />
      <ai-button theme="dark" @click="resetHandler"> {{ $t('services.reset') }}</ai-button>
      <ai-button theme="gradient" @click="goToFinetuneHandler">{{ $t('common.correction') }}</ai-button>
      <ai-button theme="gradient" @click="saveProductHandler">{{ $t('profile.save') }}</ai-button>
    </div>
  </div>

  <div v-if="imageForActions !== '' && step === 2" class="editor-finetune">
    <image-finetune
      :src="imageForActions"
      @reset="resetHandler"
      @save="savePinturaProductHandler"
      @back="goToCanvasHandler"
      :actions="['reset', 'save', 'back']"
    />
    <!-- <pintura-editor
      v-bind="editorProps"
      :src="removedBgImage"
      :utils="['finetune']"
      :enableButtonClose="false"
      :enableButtonExport="false"
      :enableButtonRevert="false"
      :previewUpscale="true"
      ref="pinturaEditorRef"
    />
    <div class="editor-finetune__bottom">
      <v-spacer />
      <ai-button theme="dark" @click="resetHandler"> {{ $t('services.reset') }}</ai-button>
      <ai-button theme="dark" @click="goToCanvasHandler">{{ $t('common.back') }}</ai-button>
      <ai-button theme="gradient" @click="savePinturaProductHandler">{{ $t('profile.save') }}</ai-button>
    </div> -->
  </div>
  <div
    v-else-if="imageForActions !== '' && imageResult === '' && step === 3"
    :class="{ 'service__container--mobile': isMobile }"
    class="service__container editor"
  >
    <product-workarea v-if="isMobile" />
    <div class="editor__settings">
      <product-settings :key="key" @key-update="keyUpdateHandler" />
    </div>
    <div class="editor__workarea">
      <product-workarea v-if="!isMobile" @key-update="keyUpdateHandler" />
      <product-tools-bottom class="editor__workarea__tools" />
    </div>
  </div>
  <result-image v-else-if="imageForActions !== '' && imageResult !== ''" />
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useCommonStore } from '@/store/common';
import { useEditorStore } from '@/store/editor';
import { useServicesStore } from '@/store/services';

import { TMode } from '@/types/TMode';

import AddImage from '@/components/common/AddImage/AddImage.vue';
import ImageFinetune from '@/components/common/ImageFinetune/ImageFinetune.vue';
import ProductSettings from '@/components/pages/Generate/ProductSettings/ProductSettings.vue';
import ProductWorkarea from '@/components/pages/Generate/ProductWorkarea/ProductWorkarea.vue';
import ProductToolsBottom from '@/components/pages/Generate/ProductToolsBottom/ProductToolsBottom.vue';
import ResultImage from '@/components/common/ResultImage/ResultImage.vue';

import AiButton from '@/components/UI/AiButton/AiButton.vue';
import AiCanvas from '@/components/UI/AiCanvas/AiCanvas.vue';
import AiCard from '@/components/UI/AiCard/AiCard.vue';
import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
// import AiDropLoader from '@/components/UI/AiDropLoader/AiDropLoader.vue';
// import AiFileUpload from '@/components/UI/AiFileUpload/AiFileUpload.vue';
import AiTooltip from '@/components/UI/AiTooltip/AiTooltip.vue';

const baseUrl = process.env.VUE_APP_BASE_URL;

const { fetchThemes } = useEditorStore();
const { imageForActions, imageResult, sourceBgImage } = storeToRefs(useServicesStore());
const { generateImageData, themesList } = storeToRefs(useEditorStore());

const { isMobile } = storeToRefs(useCommonStore());

const key = ref<number>(0);
const step = ref<number>(localStorage.getItem('step') ? Number(localStorage.getItem('step')) : 0);
const mode = ref<TMode>('erase');
const brushSize = ref<number>(110);
const aiCanvasRef = ref<any>(null);
const activeZoom = ref<number>(1);
const imageWidth = ref<number>(1);
const imageHeight = ref<number>(1);
const isFinetuneBg = ref<boolean>(false);

const keyUpdateHandler = (): void => {
  key.value += 1;
};

const zoomInHandler = (): void => {
  activeZoom.value += 0.1;
};

const zoomOutHandler = (): void => {
  activeZoom.value -= 0.1;
};

const activeZoomSetHandler = (newVal: number): void => {
  activeZoom.value = newVal;
};

const setMode = (modeVal: TMode): void => {
  mode.value = modeVal;
};

const goToCanvasHandler = (): void => {
  step.value = 1;
};

const goToFinetuneHandler = async (): Promise<void> => {
  const canvasImage = await aiCanvasRef.value.getBackground();
  imageForActions.value = canvasImage.enlargedBase64src;
  step.value = 2;
};

const saveProductHandler = async (): Promise<void> => {
  const canvasImage = await aiCanvasRef.value.getBackground();
  imageForActions.value = canvasImage.enlargedBase64src;
  step.value = 3;
  // sourceBgImage.value = '';
  // // const trimmedImage = await trimTransparent(removeBase64Prefix(canvasImage.enlargedBase64src));
  // const productId = await createProduct(canvasImage);
  // if (productId) {
  //   router.push({ name: 'Editor', params: { id: productId } });
  // }
};

const savePinturaProductHandler = async (image: string): Promise<void> => {
  // const trimmedImage = await trimTransparent(removeBase64Prefix(image));
  imageForActions.value = image;
  step.value = 1;
};

const resetHandler = (): void => {
  step.value = 0;
  isFinetuneBg.value = false;
  imageForActions.value = '';
  imageResult.value = '';
};

const fintuneBgHandler = (value: boolean): void => {
  isFinetuneBg.value = value;
};

onMounted(() => {
  fetchThemes().then((resp) => {
    themesList.value = resp;
  }).catch((err) => {
    console.error(err);
  });
  // fetchStyleList().then((resp) => {
  // stylesList.value = resp;
  // });
  localStorage.removeItem('step');
});

watch(
  () => generateImageData.value.canvas,
  () => {
    if (isMobile.value) {
      const appElem = document.querySelector('.app-template');
      if (appElem) {
        appElem.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    }
  },
  { deep: true },
);

watch(imageForActions, (newVal, oldVal) => {
  if (newVal !== '' && oldVal === '') {
    if (isFinetuneBg.value) {
      step.value = 1;
    } else {
      step.value = 3;
    }
  }
});

watch(step, (newVal) => {
  if (newVal === 1) {
    const img = new Image();
    img.onload = () => {
      imageWidth.value = img.width;
      imageHeight.value = img.height;
      key.value += 1;
    };
    img.src = imageForActions.value;
  } else if (newVal === 0) {
    imageForActions.value = '';
    imageResult.value = '';
  }
});
</script>

<style scoped lang="scss" src="./Generate.scss" />
